import axios from 'axios';

export const tmAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
});

export const tmApi = axios.create();

tmApi.interceptors.request.use(config => {
    config.headers = {
        ...config.headers,
        ...tmAuthHeaders()
    };
    return config;
});

export const ACCESS_OPTIONS = [
    { value: 'none', label: 'Нет', color: 'default' },
    { value: 'read', label: 'Чтение', color: 'blue' },
    { value: 'edit', label: 'Редактирование', color: 'orange' },
    { value: 'full', label: 'Полные', color: 'green' }
];

export const FIELD_VALUE_TYPES = [
    { value: 'string', label: 'Строка' },
    { value: 'text', label: 'Текст' },
    { value: 'number', label: 'Число' },
    { value: 'date', label: 'Дата' },
    { value: 'datetime', label: 'Дата и время' },
    { value: 'boolean', label: 'Да / Нет' },
    { value: 'select', label: 'Список (свои значения)' },
    {
        value: 'dictionary',
        label: 'Справочник (привязка сущности)'
    },
    { value: 'composite', label: 'Составное' },
    {
        value: 'repeater',
        label: 'Динамическое (множественное)'
    }
];

export const fieldTypeLabel = type =>
    FIELD_VALUE_TYPES.find(item => item.value === type)
        ?.label || type;

export const normalizeStatusKey = value =>
    String(value || '')
        .trim()
        .toLowerCase()
        .replace(/ё/g, 'е')
        .replace(/\s+/g, ' ');

export const buildStatusColorMap = statuses => {
    const map = {};

    (statuses || []).forEach(item => {
        if (!item?.name) {
            return;
        }

        map[normalizeStatusKey(item.name)] =
            item.color || '#1677ff';
    });

    return map;
};

export const MSK_TIME_ZONE = 'Europe/Moscow';

export const formatMoscowClock = (date = new Date()) =>
    date.toLocaleString('ru-RU', {
        timeZone: MSK_TIME_ZONE,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });

export const formatMoscowDateTime = (
    value,
    {
        withSeconds = true,
        dateOnly = false
    } = {}
) => {
    if (!value) {
        return '—';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    if (dateOnly) {
        return date.toLocaleDateString('ru-RU', {
            timeZone: MSK_TIME_ZONE
        });
    }

    return date.toLocaleString('ru-RU', {
        timeZone: MSK_TIME_ZONE,
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        ...(withSeconds ? { second: '2-digit' } : {})
    });
};

export const STANDARD_FILTER_FIELDS = [
    {
        key: 'status_name',
        label: 'Статус',
        type: 'status'
    },
    {
        key: 'status_invoices',
        label: 'Статус ВДР',
        type: 'select',
        options: [
            'new',
            'in_progress',
            'filled',
            'rework',
            'approval',
            'approved',
            'analysis'
        ]
    },
    {
        key: 'region_name',
        label: 'Регион (заказчик)',
        type: 'text'
    },
    {
        key: 'assigned_user_fio',
        label: 'Исполнитель',
        type: 'text'
    },
    {
        key: 'news_type_name',
        label: 'Тип работ',
        type: 'dictionary',
        dictionarySystemName: 'work_types'
    },
    {
        key: 'project_id',
        label: 'Проект',
        type: 'project'
    },
    {
        key: 'creator_org_id',
        label: 'Орг. создатель',
        type: 'org',
        orgType: 'creator'
    },
    {
        key: 'executor_org_id',
        label: 'Орг. исполнитель',
        type: 'org',
        orgType: 'executor'
    },
    {
        key: 'search',
        label: 'Поиск',
        type: 'text'
    }
];

/** Подбор орг-исполнителя по связке создатель + вид работ (+ проект). */
export const resolveAutoExecutorOrgId = ({
    creatorOrgId,
    workType,
    projectId,
    workLinks = []
}) => {
    const creatorId = Number(creatorOrgId) || 0;
    const wt = String(workType || '').trim();
    const projId = Number(projectId) || 0;
    if (!creatorId || !wt) {
        return null;
    }

    const matched = (workLinks || []).filter(link => {
        if (Number(link.creator_org_id) !== creatorId) {
            return false;
        }
        if (String(link.work_type) !== wt) {
            return false;
        }
        const linkProject = Number(link.project_id) || 0;
        if (linkProject && projId && linkProject !== projId) {
            return false;
        }
        if (linkProject && !projId) {
            return false;
        }
        return true;
    });

    if (!matched.length) {
        return null;
    }

    if (projId) {
        const specific = matched.find(
            link => Number(link.project_id) === projId
        );
        if (specific) {
            return Number(specific.executor_org_id) || null;
        }
    }

    const generic = matched.filter(link => !Number(link.project_id));
    if (generic.length === 1) {
        return Number(generic[0].executor_org_id) || null;
    }
    if (matched.length === 1) {
        return Number(matched[0].executor_org_id) || null;
    }

    return null;
};
